import { i18n } from '@/i18n';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { employeesApi } from '@/api/employees.api';
import { hrApi } from '@/api';
import { customersApi } from '@/api/customers.api';
import { suppliersApi } from '@/api/suppliers.api';
import { productsApi } from '@/api/products.api';
import { projectsApi } from '@/api/projects.api';
import type { SelectOption } from '@/components/ui/Select';
import type { EntityOptionResponse } from '@/types/entityOption';
import { formatEntityOptionLabel, formatEntityOptionShort } from '@/utils/entityLabels';
import { queryKeys, tierOptions } from '@/lib/query';
import { showApiError } from '@/utils/apiError';

type EmployeeOptionsParams = {
  activeOnly?: boolean;
  pageSize?: number;
  hrScoped?: boolean;
  enabled?: boolean;
};

export function useEmployeeOptionsQuery(params: EmployeeOptionsParams = {}) {
  const { activeOnly = true, pageSize = 50, hrScoped = false, enabled = true } = params;
  const listParams = { page: 0, size: pageSize, activeOnly, hrScoped };

  const query = useQuery({
    queryKey: queryKeys.employees.options(listParams),
    queryFn: async () => {
      try {
        const res = hrScoped
          ? await hrApi.listVisibleEmployeeOptions({ page: 0, size: pageSize, activeOnly })
          : await employeesApi.listOptions({ page: 0, size: pageSize, activeOnly });
        return res.content;
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadEmployees', { ns: 'common' }));
        throw err;
      }
    },
    enabled,
    ...tierOptions('operational'),
  });

  return useEntityOptionsResult(query.data ?? [], query.isLoading);
}

export function useCustomerOptionsQuery(pageSize = 500) {
  const params = { page: 0, size: pageSize, activeOnly: true };
  const query = useQuery({
    queryKey: queryKeys.customers.options(params),
    queryFn: async () => {
      try {
        const res = await customersApi.listOptions(params);
        return res.content;
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadCustomers', { ns: 'common' }));
        throw err;
      }
    },
    ...tierOptions('operational'),
  });

  return useEntityOptionsResult(query.data ?? [], query.isLoading);
}

export function useSupplierOptionsQuery(pageSize = 200) {
  const params = { page: 0, size: pageSize, activeOnly: true };
  const query = useQuery({
    queryKey: queryKeys.suppliers.options(params),
    queryFn: async () => {
      try {
        const res = await suppliersApi.listOptions(params);
        return res.content;
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadSuppliers', { ns: 'common' }));
        throw err;
      }
    },
    ...tierOptions('operational'),
  });

  return useEntityOptionsResult(query.data ?? [], query.isLoading);
}

export function useProductOptionsQuery(pageSize = 200) {
  const params = { page: 0, size: pageSize, activeOnly: true };
  const query = useQuery({
    queryKey: queryKeys.products.options(params),
    queryFn: async () => {
      try {
        const res = await productsApi.listOptions(params);
        return res.content;
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadProducts', { ns: 'common' }));
        throw err;
      }
    },
    ...tierOptions('operational'),
  });

  return useEntityOptionsResult(query.data ?? [], query.isLoading);
}

export function useProjectOptionsQuery(pageSize = 200) {
  const params = { page: 0, size: pageSize };
  const query = useQuery({
    queryKey: queryKeys.projects.options(params),
    queryFn: async () => {
      try {
        const res = await projectsApi.listProjectOptions(params);
        return res.content;
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadProjects', { ns: 'common' }));
        throw err;
      }
    },
    ...tierOptions('operational'),
  });

  return useEntityOptionsResult(query.data ?? [], query.isLoading);
}

function useEntityOptionsResult(options: EntityOptionResponse[], loading: boolean) {
  const selectOptions: SelectOption[] = useMemo(
    () =>
      options.map((option) => ({
        value: String(option.id),
        label: formatEntityOptionLabel(option),
      })),
    [options],
  );
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const getOption = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };
  const getLabel = (id: number | string | null | undefined, short = false) => {
    const option = getOption(id);
    if (!option) return id != null && id !== '' ? `#${id}` : '—';
    return short ? formatEntityOptionShort(option) : formatEntityOptionLabel(option);
  };
  return {
    options: selectOptions,
    loading,
    getLabel,
    byId,
    getOption,
    /** @deprecated Use getOption — kept for hook consumers expecting entity-named getters. */
    getEmployee: getOption,
    getCustomer: getOption,
    getSupplier: getOption,
    getProduct: getOption,
    getProject: getOption,
    employees: options,
    customers: options,
    suppliers: options,
    products: options,
    projects: options,
  };
}

/** @deprecated Use useEmployeeOptionsQuery — thin alias for backward compatibility. */
export const useEmployeeOptions = useEmployeeOptionsQuery;
export const useCustomerOptions = useCustomerOptionsQuery;
export const useSupplierOptions = useSupplierOptionsQuery;
export const useProductOptions = useProductOptionsQuery;
